import { ObservableInput, from } from 'rxjs';
import { createElement, ComponentType, useEffect, useState } from 'react';
import { Routine, subscribeRoutine } from '../routines';
import { action$ } from '../action$';
import { defaultErrorSubject } from '../internal';
import { useVisibleComponentTracking } from './visible-components-tracker';

export const NOT_YET_EMITTED = Symbol('Returned from rxbeach/react:useStream');
// eslint-disable-next-line @typescript-eslint/no-redeclare
type NOT_YET_EMITTED = typeof NOT_YET_EMITTED;

/**
 * React hook to subscribe to a stream
 *
 * Each emit from the stream will make the component re-render with the new
 * value. Initially, `NOT_YET_EMITTED` is returned, because an `Observable`
 * has no guarantee for when the first emit will happen. This initial
 * value may be overridden with the `defaultValue` argument.
 *
 * @param source$ Stream that provides the needed values
 * @param defaultValue Default value returned on init until stream emits new value
 */
export const useStream: {
  <T>(source$: ObservableInput<T>): T | NOT_YET_EMITTED;
  <T>(source$: ObservableInput<T>, defaultValue: T): T;
} = <T>(source$: ObservableInput<T>, defaultValue?: T): T | NOT_YET_EMITTED => {
  const [value, setValue] = useState<T | NOT_YET_EMITTED>(
    defaultValue !== undefined ? defaultValue : NOT_YET_EMITTED
  );
  const [error, setError] = useState<unknown>(null);

  if (error) {
    throw error;
  }

  useEffect(() => {
    const subscription = from(source$).subscribe({
      next: setValue,
      error: setError,
    });

    return () => subscription.unsubscribe();
  }, [source$]);

  return value;
};

/**
 * React hook to subscribe a routine with the lifecycle of the component
 *
 * The routine will be subscribed and unsubscribed from the action stream when
 * the component is mounted and unmounted.
 *
 * @param routine The routine which should use the component's lifecycle
 * @param internalAction$ The action$ the routine should subscribe to
 * @param errorSubject The subject to send errors to
 */
export const useRoutine = (
  routine: Routine<unknown> | undefined,
  internalAction$ = action$,
  errorSubject = defaultErrorSubject
) => {
  useEffect(() => {
    // We allow the routine to be undefined, because hooks shouldn't be conditional.
    if (!routine) return;
    const sub = subscribeRoutine(internalAction$, routine, errorSubject);
    return () => sub.unsubscribe();
  }, [errorSubject, internalAction$, routine]);
};

/**
 * Higher order component for providing component with props from a stream
 *
 * The stream does not need to provide all the properties the component expects,
 * any missing properties will be required by the component returned from this
 * component.
 *
 * Typescript does, in certain cases, allow for extra properties to be unpacked
 * on a component. This means it may be possible to send props which would be
 * provided by the stream. In these cases the values from the stream will
 * override any values passed as props.
 *
 * A routine can also be provided to the component. It will be subscribed and
 * unsubscribed from the action$ when the component mounts and unmounts. For
 * multiple routines, use {@link collectRoutines}.
 *
 * @param Component Component that will receive props from the stream
 * @param stream$ Stream that will provide props to the component
 * @param routine Routine which will follow the lifecycle of the component
 * @returns A component that renders the original component with props retrieved
 *  from the stream
 * @see {@link useStream}
 */
export const connect = <Props extends object, Observed extends Partial<Props>>(
  Component: ComponentType<Props>,
  stream$: ObservableInput<Observed>,
  routine?: Routine<unknown>
) =>
  Object.assign(
    (props: Omit<Props, keyof Observed>) => {
      const value = useStream(stream$);
      useRoutine(routine);

      const visibleComponentProps = (
        value === NOT_YET_EMITTED ? { ...props } : { ...props, ...value }
      ) as Props & Observed;
      useVisibleComponentTracking(Component, visibleComponentProps);

      if (value === NOT_YET_EMITTED) return null;

      // Typescript doesn't recognize this as Observed & Props for some reason
      // Question on StackOverflow: https://stackoverflow.com/q/60758084/1104307
      const newProps = { ...props, ...value } as Props & Observed;

      return createElement(Component, newProps);
    },
    { displayName: `Connect(${Component.displayName || Component.name})` }
  );

let instanceCount = 0;
const nextInstanceName = () => `view-${instanceCount++}`;

/**
 * Higher order component for connecting a component to a stream
 *
 * This is the same as {@link connect }, but takes a factory for the stream. A
 * new stream will be retrieved from the factory for every new mount of the
 * component.
 *
 * @param Component Component that will receive props from the stream
 * @param createInstanceStream Function to create the stream for each instance
 *  of the component
 * @returns A component that renders the original component with props retrieved
 *  from the stream
 * @see {@link connect}
 */
export const connectInstance = <
  Props extends object,
  Observed extends Partial<Props>,
>(
  Component: ComponentType<Props>,
  createInstanceStream: (instance: string) => ObservableInput<Observed>
) =>
  function ConnectInstance(props: Omit<Props, keyof Observed>) {
    const [stream$] = useState(() => createInstanceStream(nextInstanceName()));
    const value = useStream(stream$);

    const visibleComponentProps = (
      value === NOT_YET_EMITTED ? { ...props } : { ...props, ...value }
    ) as Props & Observed;
    useVisibleComponentTracking(Component, visibleComponentProps);

    if (value === NOT_YET_EMITTED) return null;

    // Typescript doesn't recognize this as Observed & Props for some reason
    // Question on StackOverflow: https://stackoverflow.com/q/60758084/1104307
    const newProps = { ...props, ...value } as Props & Observed;

    return createElement(Component, newProps);
  };
